/**
 * Порог шума профиля «Музыка» /denoise — по 15 полосам afftdn, а не одним числом.
 *
 * Зачем (замер 23.10.2026, docs/why/ai-sand.md): прежний порог — p05 кадрового
 * RMS + 3 дБ, поданный как БЕЛЫЙ шум. У плотной песни тихие кадры — сама музыка,
 * а белый профиль на уровне всей песни в верхних полосах выше настоящего шипения
 * на десятки дБ: «Лёгкое» снимало с чистой песни 2,6 / 5,2 / 6,2 дБ в 2–5 / 5–10 /
 * 10–16 кГц. Здесь уровень меряется в каждой полосе отдельно, и полоса считается
 * полом шипения, только если её уровень стоит ровно (p20 − p3 ≤ test) — у музыки
 * он ходит.
 *
 * Что afftdn делает с `bn` (исходник FFmpeg 5.1 и 6.0, `read_custom_noise` +
 * `reduce_mean`): это ФОРМА — каждое значение зажимается в ±24 дБ, потом из всех
 * вычитается среднее; общий уровень задаёт `nf` (белый шум RMS L dBFS ↔ nf = L).
 * Поэтому решатель не пишет уровни напрямую: он ищет такой общий уровень M, при
 * котором полосы пола стоят на своём замере, а полосы музыки — не выше своего
 * p3 − 12 дБ; не сходится — чистит меньше, но порог над музыкой не поднимает.
 *
 * Числа — перенос измеренного Python-прибора (tests/audio/ai-sand/band2.py,
 * music_floor.py) один в один, включая окно, сглаживание и «+3 дБ» нормировки;
 * совпадение держит tests/js/denoise-band-floor.test.mjs.
 */
import { fft, hann, makeTwiddles } from '../dsp-fft.js';

// Центры полос — как их считает сам afftdn (config_input): 80, затем
// lrint(1.5·c + 5) с округлением до 10 / 50 / 100 / 1000 Гц.
export const CENTRES = [80, 120, 180, 270, 410, 620, 930, 1400, 2100, 3150, 4750, 7100, 10700, 16000, 24000];
const N = 2048, HOP = 1024, EDGE = 1.224745;

/** Уровни полос по кадрам, дБ «белого эквивалента» (+3 дБ, как в приборе). */
export function bandLevels(mono, sr) {
    const w = hann(N), tw = makeTwiddles(N);
    let w2 = 0;
    for (let i = 0; i < N; i++) w2 += w[i] * w[i];
    const frames = Math.max(0, Math.floor((mono.length - N) / HOP) + 1);
    const bins = (N >> 1) + 1, binHz = sr / N;
    const ranges = CENTRES.map((c) => {
        const lo = Math.ceil((c / EDGE) / binHz - 1e-9), hiHz = Math.min(c * EDGE, sr / 2);
        let hi = lo;
        while (hi < bins && hi * binHz < hiHz) hi++;
        return [lo, Math.min(hi, bins)];
    });
    const E = CENTRES.map(() => new Float64Array(frames));
    const re = new Float64Array(N), im = new Float64Array(N);
    for (let f = 0; f < frames; f++) {
        const off = f * HOP;
        for (let i = 0; i < N; i++) { re[i] = mono[off + i] * w[i]; im[i] = 0; }
        fft(re, im, tw);
        for (let b = 0; b < CENTRES.length; b++) {
            const [lo, hi] = ranges[b];
            if (hi <= lo) { E[b][f] = 1e-12; continue; }
            let s = 0;
            for (let k = lo; k < hi; k++) s += re[k] * re[k] + im[k] * im[k];
            E[b][f] = 2 * s / w2 / (hi - lo);
        }
    }
    // Сглаживание по 4 кадрам (~85 мс) — как np.convolve(e, ones(4)/4, 'same'): нули за краями.
    return E.map((e) => {
        const out = new Float64Array(frames);
        for (let i = 0; i < frames; i++) {
            let s = 0;
            for (let k = i - 2; k <= i + 1; k++) if (k >= 0 && k < frames) s += e[k];
            out[i] = 10 * Math.log10(s / 4 + 1e-14);
        }
        return out;
    });
}

function percentile(sorted, q) {
    const pos = (q / 100) * (sorted.length - 1), i = Math.floor(pos), f = pos - i;
    return i + 1 < sorted.length ? sorted[i] + (sorted[i + 1] - sorted[i]) * f : sorted[i];
}

/**
 * Уровни полос → общий уровень M и форма bn (нулевое среднее, ±24 дБ).
 * U — потолок полосы (пол: p5 + 3; музыка: p3 − 12), kind — 'floor' | 'content' | 'empty'.
 */
export function solve(U, kind) {
    const n = U.length, cont = kind.map((k) => k !== 'empty'), fl = kind.map((k) => k === 'floor');
    if (!fl.some(Boolean)) return null;
    let minU = Infinity;
    for (let i = 0; i < n; i++) if (cont[i]) minU = Math.min(minU, U[i]);
    const Mmax = minU + 24;
    for (let step = 0; Mmax - step * 0.25 > -100; step++) {
        const M = Mmax - step * 0.25;
        const hi = U.map((u, i) => (cont[i] ? Math.min(u, M + 24) : M + 24));
        const lo = hi.map((h, i) => (fl[i] ? h : M - 24));
        const sLo = lo.reduce((a, b) => a + b, 0), sHi = hi.reduce((a, b) => a + b, 0);
        if (sLo <= n * M && n * M <= sHi) {
            const T = lo.slice();
            let need = n * M - sLo;
            const order = [];
            for (let i = 0; i < n; i++) if (!cont[i]) order.push(i);
            for (let i = 0; i < n; i++) if (cont[i] && !fl[i]) order.push(i);
            for (const i of order) {
                const d = Math.min(hi[i] - T[i], need);
                T[i] += d; need -= d;
                if (need <= 1e-9) break;
            }
            if (M < -80) return null;
            return { nf: Math.min(M, -20), bn: T.map((t) => t - M), M };
        }
    }
    return null;
}

/**
 * Замер полос — один раз на файл: p3 / p5 / p20 уровня каждой полосы.
 * channels — Float32Array каналов (анализируемый отрезок, берутся первые 10 с), sr — их частота.
 */
export function bandStats(channels, sr) {
    const len = Math.min(channels[0].length, Math.round(sr * 10));
    const mono = new Float32Array(len);
    for (const ch of channels) for (let i = 0; i < len; i++) mono[i] += ch[i] / channels.length;
    const L = bandLevels(mono, sr);
    if (!L[0].length) return null;
    const p3 = [], p5 = [], p20 = [];
    for (const row of L) {
        const s = Array.from(row).sort((a, z) => a - z);
        p3.push(percentile(s, 3)); p5.push(percentile(s, 5)); p20.push(percentile(s, 20));
    }
    return { p3, p5, p20 };
}

/**
 * Пол шипения по замеру → { nf, bn, floors } или null — «пола шипения в записи нет».
 * test — допуск ровности пола, дБ (6 — замеренное умолчание; Infinity — без проверки).
 */
export function floorFromStats(stats, { test = 6, need = 2 } = {}) {
    if (!stats) return null;
    const U = [], kind = [];
    let floors = 0;
    CENTRES.forEach((c, b) => {
        const p3 = stats.p3[b], p5 = stats.p5[b], p20 = stats.p20[b];
        if (p3 < -110) { U.push(0); kind.push('empty'); }
        else if (c >= 2000 && p20 - p3 <= test) { U.push(p5 + 3); kind.push('floor'); floors++; }
        else { U.push(p3 - 12); kind.push('content'); }
    });
    if (floors < need) return null;
    const r = solve(U, kind);
    return r ? { nf: r.nf, bn: r.bn, floors } : null;
}

/** Фильтр afftdn из пола (те же числа уходят серверному лейну). */
export function floorFilter(floor, nr) {
    return 'afftdn=nt=c:nf=' + floor.nf.toFixed(1) + ':bn=' + floor.bn.map((v) => v.toFixed(1)).join('|') + ':nr=' + nr;
}

/** Всё сразу — для тестов и приборов. */
export function bandChain(channels, sr, nr, opts = {}) {
    const floor = floorFromStats(bandStats(channels, sr), opts);
    return floor ? { filter: floorFilter(floor, nr), nf: floor.nf, bn: floor.bn, floors: floor.floors } : null;
}

if (typeof window !== 'undefined') {
    window.DenoiseBandFloor = { CENTRES, bandLevels, bandStats, floorFromStats, floorFilter, solve, bandChain };
    try { document.dispatchEvent(new Event('denoise-band-floor:ready')); } catch (_) {}
}
